import { useEffect, useState } from 'react'
import { Button } from './ui/button'
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { useToast } from './ui/toaster'
import { useWhatsAppAccounts, useStoreWhatsAppAccount } from '../hooks/useApi'

// Facebook SDK types
declare global {
  interface Window {
    FB: {
      init: (config: { appId: string; version: string; xfbml?: boolean; cookie?: boolean }) => void
      login: (callback: (response: any) => void, options?: any) => void
      getLoginStatus: (callback: (response: any) => void) => void
    }
  }
}

interface EmbeddedSignupProps {
  onSuccess?: () => void
  onCancel?: () => void
  configId?: string
}

const EmbeddedSignup = ({
  onSuccess,
  onCancel,
  configId = '1690470751765654'
}: EmbeddedSignupProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [remount, forceUpdate] = useState(0)

  const { toast } = useToast()
  const { refetch: refetchWhatsApps } = useWhatsAppAccounts()
  const storeAccountMutation = useStoreWhatsAppAccount()

  const fbLoginCallback = (response: any) => {
    console.log('sdk-response', JSON.stringify(response, null, 2))

    // Resetar estado de loading primeiro
    setIsLoading(false)

    if (response.authResponse && response.authResponse.code) {
      const code = response.authResponse.code
      setIsLoading(true)
      setStatus('loading')

      // Usar o hook com .then() e .catch() como no exemplo que funciona
      storeAccountMutation
        .mutateAsync({ code })
        .then((response) => {
          console.log('WhatsApp Business Account connected:', response)
          setStatus('success')
          setIsLoading(false)

          toast({
            title: 'Sucesso',
            description: 'Conta WhatsApp Business conectada com sucesso!',
            variant: 'success',
          })

          // Invalidar cache e recarregar lista
          refetchWhatsApps().then(() => {
            // Notificar componente pai
            onSuccess?.()
          })
        })
        .catch((error) => {
          console.error('Error connecting WhatsApp Business Account:', error)
          setStatus('error')
          setIsLoading(false)

          toast({
            title: 'Erro',
            description: 'Falha ao conectar conta WhatsApp Business',
            variant: 'destructive',
          })
        })
    } else {
      // Usuário cancelou ou fechou o popup
      setStatus('idle')
      setIsLoading(false)

      if (response.status === 'unknown' || !response.authResponse) {
        // Usuário fechou o popup sem completar
        console.log('User closed popup or cancelled login')
        onCancel?.()
      } else if (response.error) {
        // Erro no login
        console.error('Facebook login error:', response.error)
        setStatus('error')
        toast({
          title: 'Erro no Login',
          description: response.error.message || 'Falha ao fazer login no Facebook',
          variant: 'destructive',
        })
      }
    }
  }

  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      toast({
        title: 'Erro',
        description: 'Facebook SDK não carregado. Aguarde alguns segundos e tente novamente.',
        variant: 'destructive',
      })
      return
    }

    // Verificar se está em HTTPS (requerido pelo Facebook SDK)
    const isSecure = window.location.protocol === 'https:'

    if (!isSecure) {
      toast({
        title: 'HTTPS Necessário',
        description: 'O Facebook SDK requer HTTPS. Para desenvolvimento local, configure HTTPS ou use uma ferramenta como ngrok.',
        variant: 'destructive',
      })
      console.warn('Facebook SDK requires HTTPS. Current protocol:', window.location.protocol)
      return
    }

    setIsLoading(true)
    setStatus('loading')

    try {
      // Launch Facebook login - seguindo o padrão que funciona
      window.FB.login(fbLoginCallback, {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: '',
          sessionInfoVersion: '2'
        }
      })
    } catch (error: any) {
      console.error('Error launching Facebook login:', error)
      setIsLoading(false)
      setStatus('error')
      toast({
        title: 'Erro',
        description: error?.message || 'Falha ao iniciar o login do Facebook',
        variant: 'destructive',
      })
    }
  }

  useEffect(() => {
    const monitorFBEvents = (event: MessageEvent) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return
      }

      try {
        const data = JSON.parse(event.data)
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.event === 'FINISH') {
            const { phone_number_id, waba_id } = data.data
            console.log(
              'Phone number ID ',
              phone_number_id,
              ' WhatsApp business account ID ',
              waba_id
            )
            setStatus('success')
            setIsLoading(false)
          } else if (data.event === 'CANCEL') {
            const { current_step } = data.data
            console.warn('Cancel at ', current_step)
            setStatus('idle')
            setIsLoading(false)
            onCancel?.()
          } else if (data.event === 'ERROR') {
            const { error_message } = data.data
            console.error('error ', error_message)
            setStatus('error')
            setIsLoading(false)

            toast({
              title: 'Erro',
              description: `Erro no embedded signup: ${error_message}`,
              variant: 'destructive',
            })
          }
        }
        console.log('session-info-response', JSON.stringify(data, null, 2))
      } catch {
        forceUpdate((p) => p + 1)
        console.log('Non JSON Responses', event.data)
      }
    }

    window.addEventListener('message', monitorFBEvents)

    return () => {
      window.removeEventListener('message', monitorFBEvents)
    }
  }, [remount, onCancel, toast])

  const getButtonContent = () => {
    if (isLoading || status === 'loading') {
      return (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Conectando...
        </>
      )
    }

    if (status === 'success') {
      return (
        <>
          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
          Conectado
        </>
      )
    }

    if (status === 'error') {
      return (
        <>
          <AlertCircle className="w-4 h-4 mr-2 text-red-500" />
          Erro
        </>
      )
    }

    return 'WhatsApp Business'
  }

  return (
    <Button
      onClick={launchWhatsAppSignup}
      disabled={isLoading || status === 'loading'}
      variant={status === 'success' ? 'default' : 'outline'}
      className="w-full justify-start"
    >
      {getButtonContent()}
    </Button>
  )
}

export default EmbeddedSignup
